import React, { useState, useEffect } from "react";
import { Button, Modal, Typography, Input, Table, message } from "antd"; // Importing from Ant Design
import { useNavigate, useParams } from "react-router-dom";
import { InstrumentApi } from "../../services/InstrumentApi";
import { Box, Divider, Paper, styled } from "@mui/material";
import LoadingIcon from "../../reusable-component/LoadingIcon";
import { useAxios } from "../../contexts/AxiosProvider";

const InstrumentContainer = styled(Box)(({ openDrawer }) => ({
  marginLeft: openDrawer ? 240 : 80,
  width: openDrawer ? "calc(100% - 255px)" : "calc(100% - 85px)",
  height: "calc(100vh - 70px)",
}));

const EachInstrument = ({ openDrawer }) => {
  const navigate = useNavigate();
  const axios = useAxios();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [instrumentId, setInstrumentId] = useState("");
  const [fileUrlPath, setFileUrlPath] = useState("");
  const [fieldData, setFieldData] = useState([]);
  const [loading, setloading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const {
    instrumentId: paramInstrumentId,
    instrumentName: paramInstrumentName,
  } = useParams();

  useEffect(() => {
    if (paramInstrumentId) {
      setInstrumentId(paramInstrumentId.trim());
    }
  }, [paramInstrumentId]);

  useEffect(() => {
    if (instrumentId) {
      getEachInstrumentById();
      checkInstrumentUsage();
    }
  }, [instrumentId]);

  const getEachInstrumentById = async () => {
    try {
      setloading(true);
      const response = await InstrumentApi.getEachInstrumentById(
        axios,
        instrumentId
      );
      setFieldData(response.data);
    } catch (error) {
      console.error("Error fetching field data:", error);
    } finally {
      setloading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const checkInstrumentUsage = async () => {
    try {
      const response = await InstrumentApi.checkInstrumentUsage(
        axios,
        paramInstrumentName
      );
      setIsDisabled(response.data); // Disables the button if the API returns true
    } catch (error) {
      console.error("Error checking Data Source usage:", error);
    }
  };

  const handleBrowseClick = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".csv";
    fileInput.onchange = handleFileInputChange;
    fileInput.click();
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile({ name: file.name, file: file, url: null });
    } else {
      message.error("Only CSV files are allowed");
    }
  };

  const handleUploadClick = async () => {
    if (selectedFile || fileUrlPath) {
      const formData = new FormData();
      if (fileUrlPath) {
        formData.append("filePath", fileUrlPath);
      } else if (selectedFile.url) {
        formData.append("filePath", selectedFile.url);
      } else {
        formData.append("file", selectedFile.file);
      }
      try {
        setloading(true);
        const response = await InstrumentApi.sendDataToAPI(
          axios,
          formData,
          instrumentId
        );
        // console.log("File uploaded successfully:", response.data);
        message.success("File uploaded successfully");
        getEachInstrumentById(); // Refresh data after upload
      } catch (error) {
        // console.error("Error uploading file:", error);
        message.error("Error uploading file");
      } finally {
        setloading(false);
      }
    }
    setOpenDialog(false);
  };

  const handleAddFileClick = () => {
    setOpenDialog(true);
  };

  const handleUrlChange = (event) => {
    const url = event.target.value;
    setFileUrlPath(url);
  };

  const InstrumentColumns = [
    {
      title: "Field Name",
      dataIndex: "fieldName",
      key: "fieldName",
    },
    {
      title: "Data Type",
      dataIndex: "dataType",
      key: "dataType",
    },
  ];

  return (
    <InstrumentContainer openDrawer={openDrawer}>
      <Paper
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: 1,
        }}
      >
        <Button style={{ marginLeft: 5 }} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography
          variant="h1"
          gutterBottom
          style={{
            fontWeight: "bold",
            fontSize: 30,
            color: "#004e89",
            marginRight: "16%",
          }}
        >
          {paramInstrumentName}
        </Typography>
        <Typography
          variant="h1"
          gutterBottom
          style={{ fontWeight: "bold", fontSize: 32, color: "blue" }}
        />
      </Paper>
      <Divider />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
          mr: 1,
        }}
      >
        <Button
          type="primary"
          onClick={handleAddFileClick}
          disabled={isDisabled}
        >
          {fieldData.length === 0
            ? "Add Data Source Data"
            : "Edit Data Source Data"}
        </Button>
      </Box>

      <div
        style={{
          // position: "relative",
          maxHeight: "75%",
          overflowY: "auto",
          marginTop: "10px",
        }}
      >
        <Table
          columns={InstrumentColumns}
          bordered
          dataSource={fieldData}
          pagination={false}
          sticky
        />
      </div>

      <Modal
        open={openDialog}
        onCancel={handleDialogClose}
        footer={null}
        style={{ display: "flex", flexDirection: "column" }}
      >
        <Typography.Title
          level={5}
          style={{ display: "flex", justifyContent: "center", marginTop: -10 }}
        >
          Select File
        </Typography.Title>
        <div style={{ marginBottom: 20 }}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
            id="file-upload-input"
          />
          <label htmlFor="file-upload-input">
            <Button type="primary" component="span" onClick={handleBrowseClick}>
              Browse
            </Button>
          </label>
        </div>
        {selectedFile && (
          <Typography.Text style={{ color: "green", fontSize: "small" }}>
            Selected file: {selectedFile.name}
          </Typography.Text>
        )}
        <Input
          value={fileUrlPath}
          onChange={handleUrlChange}
          placeholder="Data Source S3 Path"
          style={{ marginBottom: 7 }}
        />
        <Typography style={{ color: "red", fontSize: "small" }}>
          *Only csv files Allowed
        </Typography>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
          }}
        >
          <Button onClick={handleDialogClose} style={{ marginRight: 10 }}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleUploadClick}>
            Upload
          </Button>
        </div>
      </Modal>
      {loading && <LoadingIcon />}
    </InstrumentContainer>
  );
};

export default EachInstrument;
